"use strict";
/**
 * 主题档位与价格带。
 *
 * 三个概念必须分开（《主题市场方案》§3.1）：
 *   档位 = 谁能买（basic / plus / premium，跟套餐分层对齐）
 *   价格 = 这套卖多少（一主题一价）
 *   状态 = 能不能被看到（draft / review / published …）
 *
 * 混成一个字段的后果很具体：把某套主题从 ¥39 调到 ¥49，如果"能否被成长版
 * 客户看到"是按价格算的，这一定价动作就悄悄把它从客户眼前拿走了 ——
 * 运营以为只改了价，实际改了货架。
 *
 * 所以这里只做两件事：给档位配一个价格带（防"basic 卖 ¥199、premium 卖 ¥29"
 * 这种一眼荒谬的目录），以及把年付价的算法钉死（手填对不上直接拒）。
 * 判定"谁能买"是 `THEME_TIER_ACCESS` 的事，不在这个文件里。
 */
Object.defineProperty(exports, "__esModule", { value: true });
exports.MONTHS_PER_YEAR = exports.ANNUAL_DISCOUNT = exports.THEME_TIER_PRICE_BAND = exports.THEME_TIER_LABEL = exports.THEME_TIERS = void 0;
exports.isThemeTier = isThemeTier;
exports.annualCentsOf = annualCentsOf;
exports.tierOfPrice = tierOfPrice;
exports.checkPriceBand = checkPriceBand;
exports.THEME_TIERS = ['basic', 'plus', 'premium'];
function isThemeTier(value) {
    return typeof value === 'string' && exports.THEME_TIERS.includes(value);
}
/** 界面上怎么念这个档位。放这里是为了后台审核页与控制台市场页不会各叫一个名 */
exports.THEME_TIER_LABEL = {
    basic: { zh: '基础', en: 'Basic' },
    plus: { zh: '进阶', en: 'Plus' },
    premium: { zh: '旗舰', en: 'Premium' },
};
/**
 * 档位价格带（分 / 月）。**方向只有一个**：档位越高，区间越高。
 *
 * basic 恒为 0：基础档是套餐内含的，一旦在这里挂价，客户会读到
 * "买了启航版还要再买主题"，而那正是分层卖点要避免的观感。
 *
 * 上界留了余量而不是贴着现价写：plus 现价 ¥39、premium 现价 ¥99，
 * 带子分别开到 ¥49 与 ¥129 —— 调价是运营动作，改一次价不该顺带改一次代码。
 * 真要越过上界（比如 premium 涨到 ¥199），那是一次需要评审的定价结构调整，
 * 走校验报错比让目录自己漂移好。
 */
exports.THEME_TIER_PRICE_BAND = {
    basic: { min: 0, max: 0 },
    plus: { min: 1900, max: 4900 },
    premium: { min: 6900, max: 12900 },
};
/**
 * 年付的折扣系数：按十个月收。
 *
 * 写成常量而不是让员工手算，是因为"月付 ¥39、年付 ¥374"这种数一旦手打错一位，
 * 校验器只能拦"算错"，拦不住"设计稿里写的就是错的那个数"。
 * 唯一出处在这里，令牌包、上传脚本、控制台都读它。
 */
exports.ANNUAL_DISCOUNT = 0.8;
exports.MONTHS_PER_YEAR = 12;
/**
 * 由月付价推出应付的年付价（分）。
 *
 * 算完抹到整元。理由不是"好看"而是"读得懂"：¥39 × 12 × 0.8 = ¥374.40，
 * 账单上出现毛角分会被当成算错，客服解释不清。抹到整元后是 ¥374，
 * 与对外报价口径一致（方案 §5.6 表里写的就是 ¥374 与 ¥950）。
 */
function annualCentsOf(monthlyCents) {
    const raw = monthlyCents * exports.MONTHS_PER_YEAR * exports.ANNUAL_DISCOUNT;
    return Math.round(raw / 100) * 100;
}
/**
 * 这个月价属于哪个档位。落在带与带之间的缝隙（例如 ¥59）时返回 null ——
 * 那说明定价本身要重定，而不是"就近归到某一档"。
 */
function tierOfPrice(monthlyCents) {
    for (const tier of exports.THEME_TIERS) {
        const band = exports.THEME_TIER_PRICE_BAND[tier];
        if (monthlyCents >= band.min && monthlyCents <= band.max)
            return tier;
    }
    return null;
}
/**
 * 校验「这个档位能不能定这个价」，顺带把年付价一起对。
 *
 * 年付价是可选项（本期只按月卖，`ThemePricing.annualCents` 说明过原因），
 * 但只要填了就必须等于 `annualCentsOf(月付)` —— 填一个对不上的数，
 * 等于在账单里留一颗"到底按哪个收"的雷。
 */
function checkPriceBand(tier, monthlyCents, annualCents) {
    if (!Number.isInteger(monthlyCents) || monthlyCents < 0) {
        return { ok: false, reason: `月付价必须是「分」为单位的非负整数，收到 ${monthlyCents}` };
    }
    const band = exports.THEME_TIER_PRICE_BAND[tier];
    if (monthlyCents < band.min || monthlyCents > band.max) {
        const fmt = (v) => `¥${(v / 100).toFixed(2).replace(/\.00$/, '')}`;
        return {
            ok: false,
            reason: `${tier} 档的月付价必须落在 ${fmt(band.min)} ~ ${fmt(band.max)}，` +
                `收到 ${fmt(monthlyCents)}。档位只回答"谁能买"，价格回答"多少钱"，` +
                `两者各自有约束；价格带越界时应该换档位，而不是硬塞进这一档。`,
        };
    }
    if (annualCents !== undefined) {
        const expected = annualCentsOf(monthlyCents);
        if (annualCents !== expected) {
            return {
                ok: false,
                reason: `年付价应为 round(${monthlyCents} × 12 × ${exports.ANNUAL_DISCOUNT}) = ${expected} 分，收到 ${annualCents} 分`,
            };
        }
    }
    return { ok: true, reason: '' };
}
//# sourceMappingURL=pricing.js.map